import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Types for verse notes
export interface VerseNote {
  id: string;
  user_id: string;
  verse_id: string;
  content: string;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
  deleted: boolean;
}

// Function to save a note
export async function saveNote(verseId: string, content: string) {
  const user = supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('verse_notes')
    .upsert({
      verse_id: verseId,
      content: content,
      user_id: (await user).data.user?.id,
      updated_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id,verse_id'
    });

  if (error) throw error;
  return data;
}

// Function to get notes for multiple verses
export async function getVerseNotesBatch(verseIds: string[]) {
  const user = supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .rpc('get_verse_notes_batch', {
      p_user_id: (await user).data.user?.id,
      p_verse_ids: verseIds,
    });

  if (error) throw error;
  return data as VerseNote[];
}

// Function to delete a note
export async function deleteNote(verseId: string) {
  const user = supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('verse_notes')
    .delete()
    .match({ 
      verse_id: verseId, 
      user_id: (await user).data.user?.id 
    });

  if (error) throw error;
}

// Function to get all notes for a user
export async function getAllNotes() {
  const user = supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('verse_notes')
    .select('*')
    .eq('user_id', (await user).data.user?.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as VerseNote[];
}
